import React, { useState, useRef, useMemo, useCallback } from "react";
import { FilterItem } from "./filterItems";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import useCustomActivityStore from "@/stores/tripBuilder/useCustomActivity";
import { ProgressBar } from "@tripbuilder/common/ProgressBar";
import { questions } from "./custom/question";

interface GeneralExperience {
  name: string;
  address: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  id: string;
}

interface SearchInputProps {
  activeExperiencesTab: "activities" | "experiences" | "add-activity";
  filterItems: { icon: string; name: string }[];
  selectedFilter: string | null;
  setSelectedFilter: (filter: string | null) => void;
  setSelectedExperience: (details: any) => void;
}

const SearchInputComponent = React.memo<
  SearchInputProps & {
    dropdownRef: React.RefObject<HTMLDivElement>;
    isOpen: boolean;
    toggleDropdown: () => void;
  }
>(
  ({
    activeExperiencesTab,
    filterItems,
    selectedFilter,
    setSelectedFilter,
    setSelectedExperience,
    dropdownRef,
    isOpen,
    toggleDropdown,
  }) => {
    return (
      <div
        ref={dropdownRef}
        className="search-input relative w-full mt-2 flex flex-row items-center space-x-2"
      >
        <div className="relative w-full">
          <input
            type="text"
            disabled={true}
            placeholder="Search coming soon..."
            className="w-full py-1.5 rounded-2xl bg-neutral-100 text-sm text-neutral-800 pl-10 pr-4 focus:outline-0 focus:ring-[1.7px] focus:ring-indigo-700 transition-all duration-300 ease-in-out"
          />
          <Search className="w-5 h-5 absolute left-2.5 top-1/2 transform -translate-y-1/2 text-neutral-400" />
        </div>

        {/* Dropdown menu */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="absolute top-full left-0 w-full bg-white mt-2 rounded-2xl shadow-xl z-20 overflow-hidden"
          >
            <h2 className="text-[15px] font-bold text-neutral-900 px-3.5 pt-3">
              Quick Picks
            </h2>
            <div className="grid grid-cols-4 gap-x-1 gap-y-2 p-2">
              {filterItems.map((item, index) => (
                <FilterItem
                  key={index}
                  icon={item.icon}
                  name={item.name}
                  selectedFilter={selectedFilter}
                  setSelectedFilter={setSelectedFilter}
                  toggleDropdown={toggleDropdown}
                />
              ))}
            </div>
          </motion.div>
        )}
      </div>
    );
  }
);

interface SearchInputContainerProps {
  activeExperiencesTab: "activities" | "experiences" | "add-activity";
  filterItems: { icon: string; name: string }[];
  selectedFilter: string | null;
  setSelectedFilter: (filter: string | null) => void;
  setSelectedExperience: (details: any) => void;
}

const SearchInput: React.FC<SearchInputContainerProps> = ({
  activeExperiencesTab,
  filterItems,
  selectedFilter,
  setSelectedFilter,
  setSelectedExperience,
}) => {
  // const { selectedLocation } = useTripStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentStep = useCustomActivityStore((state) => state.currentStepIndex);
  const totalSteps = questions.length;

  const toggleDropdown = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const MemoizedSearchInputComponent = useMemo(
    () => (
      <SearchInputComponent
        activeExperiencesTab={activeExperiencesTab}
        filterItems={filterItems}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        setSelectedExperience={setSelectedExperience}
        dropdownRef={dropdownRef}
        isOpen={isOpen}
        toggleDropdown={toggleDropdown}
      />
    ),
    [
      activeExperiencesTab,
      filterItems,
      selectedFilter,
      setSelectedFilter,
      setSelectedExperience,
      isOpen,
      toggleDropdown,
    ]
  );

  if (activeExperiencesTab === "add-activity") {
    return <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />;
  }

  return MemoizedSearchInputComponent;
};

export default SearchInput;
